import { useCallback, useRef, useState } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { buildActivityPayload } from "@hooks/hookscatalog/Activity/buildActivityPayload";
import { mapWizardDataToInputForm } from "../AddActivity/payload/mapWizardDataToInputForm";
import { mapWizardDataToCourseInputForm } from "../Course/payload/mapWizardDataToCourseInputForm";
import { buildCoursePayload } from "../Course/payload/buildCoursePayload";
import { mapWizardDataToPathInputForm } from "../Path/payload/mapWizardDataToPathInputForm";
import { buildPathPayload } from "../Path/payload/buildPathPayload";

export type DraftType = "activity" | "course" | "path";

export interface DraftResponse {
  id: number;
  data?: Record<string, any>;
}

interface InitiativeAssociation {
  id: number;
  category_ids: number[];
  topic_ids: number[];
}

const DRAFT_CREATE_KEY: Record<
  DraftType,
  "v2DraftActivity" | "v2DraftCourse" | "v2DraftPath"
> = {
  activity: "v2DraftActivity",
  course: "v2DraftCourse",
  path: "v2DraftPath",
};

const buildLoPayload = (type: DraftType, data: any): any => {
  switch (type) {
    case "activity":
      return buildActivityPayload(mapWizardDataToInputForm(data));
    case "course":
      return buildCoursePayload(mapWizardDataToCourseInputForm(data));
    case "path":
      return buildPathPayload(mapWizardDataToPathInputForm(data));
    default:
      return {};
  }
};

const buildInitiativeAssociations = (data: any): InitiativeAssociation[] => {
  if (!Array.isArray(data?.selected_initiatives)) return [];
  const editorial = data?.editorial_per_initiative ?? {};
  return data.selected_initiatives
    .map((it: any) => Number(it?.id))
    .filter((id: number) => Number.isFinite(id))
    .map((id: number) => ({
      id,
      category_ids: Array.isArray(editorial[id]?.selected_carousel_ids)
        ? editorial[id].selected_carousel_ids
        : [],
      topic_ids: Array.isArray(editorial[id]?.selected_topic_ids)
        ? editorial[id].selected_topic_ids
        : [],
    }));
};

const buildDraftBody = (type: DraftType, data: any): any => {
  const payload = buildLoPayload(type, data) || {};
  return {
    ...payload,
    initiatives: buildInitiativeAssociations(data),
    __draft: {
      type,
      wizard: data,
    },
  };
};

interface UseSaveDraftArgs {
  corporateId: string;
  type: DraftType;
  initialDraftId?: number | null;
}

export const useSaveDraft = ({
  corporateId,
  type,
  initialDraftId = null,
}: UseSaveDraftArgs) => {
  const [draftId, setDraftId] = useState<number | null>(initialDraftId);
  const draftIdRef = useRef<number | null>(initialDraftId);
  const inFlightRef = useRef<Promise<DraftResponse> | null>(null);
  const { mutate: mutateCache } = useSWRConfig();

  const createUrl = ServicesURL[DRAFT_CREATE_KEY[type]].replace(
    ":idCorporate",
    corporateId,
  );

  const { trigger, isMutating } = useSWRMutation<
    DraftResponse,
    Error,
    string,
    any
  >(createUrl, async (_key, { arg }) => {
    const body = buildDraftBody(type, arg);
    const currentId = draftIdRef.current;
    const url =
      currentId != null
        ? ServicesURL.v2DraftById
            .replace(":idCorporate", corporateId)
            .replace(":id", String(currentId))
        : createUrl;
    const method = currentId != null ? MethodType.PUT : MethodType.POST;
    return fetcher<DraftResponse>(url, method, body);
  });

  const updateDraftId = useCallback((id: number | null) => {
    draftIdRef.current = id;
    setDraftId(id);
  }, []);

  const saveDraft = useCallback(
    async (data: any): Promise<DraftResponse> => {
      if (inFlightRef.current) return inFlightRef.current;

      const wasCreate = draftIdRef.current == null;
      const run = (async () => {
        const res = await trigger(data);
        const id = Number(res?.id);
        if (Number.isFinite(id)) {
          draftIdRef.current = id;
          setDraftId(id);
          const detailUrl = ServicesURL.v2DraftById
            .replace(":idCorporate", corporateId)
            .replace(":id", String(id));
          void mutateCache(detailUrl, res?.data ? res : undefined, {
            revalidate: false,
          });
        } else if (wasCreate) {
          throw new Error("Draft create returned no usable id");
        }
        return res;
      })();

      inFlightRef.current = run;
      try {
        return await run;
      } finally {
        inFlightRef.current = null;
      }
    },
    [trigger, corporateId, mutateCache],
  );

  return {
    saveDraft,
    isSavingDraft: isMutating,
    draftId,
    setDraftId: updateDraftId,
  };
};
